import React, { useState, useEffect, useCallback } from 'react';
import MaterialTable from "material-table";
import CircularLoading from "../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import moment from 'moment/min/moment-with-locales';
import { colors } from '../components/Theme/WebTheme';
import { SECONDORY_COLOR, MAIN_COLOR, FONT_FAMILY } from "../common/sharedFunctions";
import { ThemeProvider } from '@mui/material/styles';
import theme from "styles/tableStyle";
import AlertDialog from "../components/AlertDialog";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';

const money = (value) => `${Number(value || 0).toLocaleString('tr-TR')} TL`;

const PaymentIssues = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settingsdata.settings);
  const { fetchPaymentIssues, resolvePaymentIssue } = api;

  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });
  const [confirm, setConfirm] = useState({ open: false, row: null, action: null });
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const result = await dispatch(fetchPaymentIssues());
    if (result && result.success) {
      setIssues(result.issues || []);
    } else {
      setCommonAlert({ open: true, msg: result?.error || t('update_failed') });
      setIssues([]);
    }
    setLoading(false);
  }, [dispatch, fetchPaymentIssues, t]);

  useEffect(() => { load(); }, [load]);

  // The three decisions an admin can make. "netOwed" is computed by the server from the
  // wallet ledger, so a row can only offer the action that its real money position allows.
  const askConfirm = (row, action) => { setNote(""); setConfirm({ open: true, row, action }); };

  const runAction = async () => {
    const { row, action } = confirm;
    setConfirm({ open: false, row: null, action: null });
    if (!row) return;
    if (!settings.AllowCriticalEditsAdmin) {
      setCommonAlert({ open: true, msg: t('demo_mode') });
      return;
    }
    setLoading(true);
    const result = await dispatch(resolvePaymentIssue({
      bookingId: row.bookingId, customerUid: row.customerUid, action, note,
    }));
    setLoading(false);
    setCommonAlert({
      open: true,
      msg: result && result.success
        ? (action === 'close'
            ? t('payment_issue_closed')
            : t('payment_issue_done', { amount: money(Math.abs(result.movedAmount || 0)), balance: money(result.walletBalance) }))
        : (result?.error || t('update_failed')),
    });
    load();
  };

  const columns = [
    {
      title: t('status'), field: 'state', editable: 'never',
      render: (row) => row.state === 'RESOLVED'
        ? <Chip size="small" label={t('payment_issue_resolved')} style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }} />
        : <Chip size="small" label={t('payment_issue_open')} style={{ backgroundColor: '#FFEBEE', color: '#C62828' }} />
    },
    { title: t('name'), field: 'customerName', editable: 'never',
      render: (row) => <span>{row.customerName || '-'}<br /><span style={{ fontSize: 11, color: colors.HEADER }}>{row.customerEmail || ''}</span></span> },
    { title: t('amount'), field: 'tripCost', editable: 'never', render: (row) => money(row.tripCost) },
    { title: t('payment_issue_charged'), field: 'chargedAmount', editable: 'never', render: (row) => money(row.chargedAmount) },
    { title: t('payment_issue_refunded'), field: 'refundedAmount', editable: 'never', render: (row) => money(row.refundedAmount) },
    {
      title: t('payment_issue_net'), field: 'netOwed', editable: 'never',
      render: (row) => {
        if (row.netOwed > 0) return <b style={{ color: '#C62828' }}>{money(row.netOwed)} {t('payment_issue_owed')}</b>;
        if (row.netOwed < 0) return <b style={{ color: '#EF6C00' }}>{money(Math.abs(row.netOwed))} {t('payment_issue_overpaid')}</b>;
        return <span style={{ color: colors.HEADER }}>{t('payment_issue_nothing')}</span>;
      }
    },
    { title: t('requestDate'), field: 'createdAt', defaultSort: 'desc', editable: 'never',
      render: (row) => row.createdAt ? moment(row.createdAt).format('lll') : '-' },
  ];

  const confirmText = () => {
    const { row, action } = confirm;
    if (!row) return '';
    if (action === 'refund') return t('payment_issue_confirm_refund', { name: row.customerName || row.customerEmail || '', amount: money(row.netOwed) });
    if (action === 'reverse') return t('payment_issue_confirm_reverse', { name: row.customerName || row.customerEmail || '', amount: money(Math.abs(row.netOwed)) });
    return t('payment_issue_confirm_close');
  };

  if (loading) return <CircularLoading />;

  const openCount = issues.filter((i) => i.state !== 'RESOLVED').length;

  return (
    <ThemeProvider theme={theme}>
      <Typography style={{ margin: '4px 0 14px', color: colors.HEADER, fontFamily: FONT_FAMILY, fontSize: 14 }}>
        {t('payment_issue_hint', { count: openCount })}
      </Typography>
      <MaterialTable
        title={t('payment_issues_title')}
        columns={columns}
        data={issues}
        onRowClick={(evt, row) => setSelectedRow(row.tableData.id)}
        style={{
          direction: isRTL === "rtl" ? "rtl" : "ltr",
          borderRadius: "8px",
          boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
          padding: "20px",
        }}
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 15, 20],
          exportButton: { csv: settings.AllowCriticalEditsAdmin, pdf: false },
          rowStyle: (row) => ({ backgroundColor: selectedRow === row.tableData.id ? colors.ROW_SELECTED : colors.WHITE }),
          headerStyle: {
            position: "sticky", top: "0px", fontSize: "0.8em", fontWeight: 'bold ',
            color: colors.BLACK, backgroundColor: SECONDORY_COLOR, textAlign: "center",
            border: `1px solid ${colors.TABLE_BORDER}`, paddingRight: "20px", paddingLeft: "20px",
          },
          cellStyle: { border: `1px solid ${colors.TABLE_BORDER}`, textAlign: "center" },
          actionsColumnIndex: -1,
        }}
        localization={{
          toolbar: { searchPlaceholder: t('search'), exportTitle: t('export') },
          header: { actions: t('actions') },
          body: { emptyDataSourceMessage: t('payment_issue_empty') },
          pagination: {
            labelDisplayedRows: ('{from}-{to} ' + t('of') + ' {count}'),
            firstTooltip: t('first_page_tooltip'), previousTooltip: t('previous_page_tooltip'),
            nextTooltip: t('next_page_tooltip'), lastTooltip: t('last_page_tooltip'),
          },
        }}
        actions={[
          { icon: 'refresh', tooltip: t('payment_issue_refresh'), isFreeAction: true, onClick: load },
          (row) => ({
            icon: 'undo', tooltip: t('payment_issue_refund'),
            disabled: row.state === 'RESOLVED' || row.netOwed <= 0,
            onClick: (e, r) => askConfirm(r, 'refund'),
          }),
          (row) => ({
            icon: 'settings_backup_restore', tooltip: t('payment_issue_reverse'),
            disabled: row.state === 'RESOLVED' || row.netOwed >= 0,
            onClick: (e, r) => askConfirm(r, 'reverse'),
          }),
          (row) => ({
            icon: 'done', tooltip: t('payment_issue_close'),
            disabled: row.state === 'RESOLVED',
            onClick: (e, r) => askConfirm(r, 'close'),
          }),
        ]}
      />

      <Dialog open={confirm.open} onClose={() => setConfirm({ open: false, row: null, action: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('payment_issue_confirm_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ whiteSpace: 'pre-line' }}>{confirmText()}</DialogContentText>
          <TextField
            label={t('payment_issue_note')} value={note} onChange={(e) => setNote(e.target.value)}
            fullWidth multiline rows={2} style={{ marginTop: 16 }}
            helperText={t('payment_issue_note_help')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm({ open: false, row: null, action: null })}>{t('cancel')}</Button>
          <Button onClick={runAction} style={{ color: MAIN_COLOR, fontWeight: 'bold' }}>{t('payment_issue_apply')}</Button>
        </DialogActions>
      </Dialog>

      <AlertDialog open={commonAlert.open} onClose={() => setCommonAlert({ open: false, msg: "" })}>
        {commonAlert.msg}
      </AlertDialog>
    </ThemeProvider>
  );
};

export default PaymentIssues;
